import { observer } from 'mobx-react';
import React, { FC, useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { RootStoreContext } from '@stores/rootStore';
import { LFEChart } from '@app/_components/lf-graph/LFEChart';
import { SunburstSeriesOption } from 'echarts';
import { convertSqmToAreaByUnit, isPresent } from '@farmai-shared/LFUtils';
import { plantingGraphColors } from '../graph';
import { MockPlantingSunburst } from './Mock';
import { Legend } from '@app/_components/lf-graph/Legend';
import { PlantingResponse } from '@services/responseTypes';
const allocateProportionally = (parent: number, children: {
  [k: string]: number;
}) => {
  const total = Object.values(children).reduce((sum, value) => sum + value, 0);
  if (total === 0 || total <= parent) {
    return children;
  }
  const proportions = Object.keys(children).map(key => [key, children[key] / total * parent]);
  return Object.fromEntries(proportions);
};
const restructureData = (initialData: PlantingResponse['totals']) => ({
  created: {
    value: initialData.created > 0 ? initialData.created : initialData.planted + initialData.not_planted,
    children: {
      planted: initialData.planted > 0 ? initialData.planted : initialData.harvested + initialData.ready_to_harvest,
      not_planted: initialData.not_planted
    }
  },
  planted: {
    value: initialData.planted > 0 ? initialData.planted : initialData.harvested + initialData.ready_to_harvest,
    children: {
      harvested: initialData.harvested,
      ready_to_harvest: initialData.ready_to_harvest
    }
  },
  not_planted: {
    value: initialData.not_planted,
    children: {}
  },
  harvested: {
    value: initialData.harvested,
    children: {}
  },
  ready_to_harvest: {
    value: initialData.ready_to_harvest,
    children: {}
  }
});
type PlantingResponseKeys = 'created' | 'planted' | 'not_planted' | 'harvested' | 'ready_to_harvest';
const sunburstColors = {
  ...plantingGraphColors,
  not_planted: '#fff'
};
const createDataHandler = (initialData: PlantingResponse['totals']) => {
  const restructured = restructureData(initialData);
  const storedData = restructured;
  const handleInternalValue = (parent: PlantingResponseKeys, current: PlantingResponseKeys) => {
    const children = storedData[parent].children;
    const allocated = allocateProportionally(storedData[parent].value, children);
    const currentRatioToParent = initialData![parent] <= initialData![current] || storedData[parent].value >= storedData[current].value ? 1 : initialData![current] / initialData![parent];
    const newValue = allocated[current] * currentRatioToParent;
    storedData[current].value = newValue;
    return newValue;
  };
  return handleInternalValue;
};
export const PlantingSunburst: FC<{
  data: PlantingResponse['totals'] | undefined;
  hasDataToDisplay?: boolean;
  mock?: boolean;
}> = observer(({
  data,
  hasDataToDisplay = true,
  mock = false
}) => {
  const {
    userStore
  } = useContext(RootStoreContext);
  const {
    t
  } = useTranslation();
  const areaUnit = userStore.user.settings.area_unit;
  if (!hasDataToDisplay || !isPresent(data)) {
    return <MockPlantingSunburst title={t('noGraphData')} />;
  }
  const legendData = Object.keys(sunburstColors).map(key => ({
    label: key,
    svg: <rect key={key} // key is necessary for eslint
    width={16} height={16} rx={4} fill={sunburstColors[key]} stroke="#BFC7E1"></rect>,
    value: data[key],
    extra: <p className="text-lg">
        {convertSqmToAreaByUnit(data[key], areaUnit, {
        computed: true
      })}
        <span className="ml-2">{t(areaUnit)}</span>
      </p>
  }));
  const dataHandler = createDataHandler(data);
  return <div className={`
      keep-together flex h-full flex-col rounded-xl bg-smoke p-12
      print:break-inside-avoid print:px-0 print:py-12
    `}>


    <h1 className={`
  hidden text-3xl
  print:my-8 print:block print:text-4xl print:font-bold
`}>{t('totalArea')}</h1>
      <div className={`
        h-88
        xl:h-144
      `}>
        <LFEChart series={{
        type: 'sunburst',
        data: [{
          name: t('created'),
          value: data.created > 0 ? data.created : data.planted + data.not_planted,
          itemStyle: {
            color: plantingGraphColors['created']
          },
          children: [{
            name: t('planted'),
            value: dataHandler('created', 'planted'),
            itemStyle: {
              color: plantingGraphColors['planted']
            },
            children: [{
              name: t('harvested'),
              value: dataHandler('planted', 'harvested'),
              itemStyle: {
                color: plantingGraphColors['harvested']
              }
            }, {
              name: t('ready_to_harvest'),
              value: dataHandler('planted', 'ready_to_harvest'),
              itemStyle: {
                color: plantingGraphColors['ready_to_harvest']
              }
            }]
          }, {
            name: t('not_planted'),
            value: dataHandler('created', 'not_planted'),
            itemStyle: {
              color: mock ? '#E5E9F3' : '#fff' // white not clear on mock
            }
          }]
        }],
        radius: [0, '100%'],
        label: {
          show: false
        },
        nodeClick: false,
        cursor: 'default'
      } as SunburstSeriesOption} />
      </div>

      <Legend className="mx-auto flex w-fit flex-col gap-y-4" data={legendData.filter(d => parseFloat(d.value) !== 0)} />
    </div>;
});